import React from 'react';

import {BaseDropdownInput, type BaseDropdownProps} from '../BaseDropdownInput/BaseDropdownInput';

/**
 * The `ComponentProps` interface defines the properties for the `DropdownInput` component.
 * This interface specifies all the necessary props for creating a customizable dropdown select input
 * that allows users to choose from a list of predefined options with support for default values,
 * disabled states, and flexible positioning of the dropdown menu.
 */
export type ComponentProps = Omit<BaseDropdownProps, 'hasSearch'>;

/**
 * The `DropdownInputInput` component renders a customizable select input with options, which can be disabled and allows users to
 * choose an option from a list. The component also provides a popover list of options with animation and handles the user's selection
 * with the provided `onChange` callback.
 *
 * @param props                 The component props.
 * @param props.defaultValue    The default selected value for the select input.
 * @param props.isDisabled      Optional. Determines whether the select input is disabled. Defaults to `false`.
 * @param props.inputLabel      The label for the select dropdown.
 * @param props.onChange        The callback function to handle changes when the user selects a new option.
 * @param props.options         An array of options to be rendered in the select input, each having a label and value.
 * @param props.hints           Optional. An array of hint messages to display below the select input.
 * @param props.infoText        Optional. Informational text to provide additional context about the select input.
 * @param props.popOverPosition Optional. The position of the popover list that displays the options. Defaults to `'right'`.
 * @param props.testId          Optional. A unique identifier for testing purposes. Defaults to `'DropdownInputInput'`.
 * @param props.name            The name identifier for the form field, used for form validation and field tracking.
 * @param props.placeholder     Optional. Placeholder text to display when no option is selected.
 * @param props.isKeepOnUnmount Optional. Determines whether to keep the form field value when the component unmounts. Defaults to `false`.
 * @param props.validation      Optional validation function to validate the selected value.
 * @returns A JSX element representing the filter select input with animated popover options.
 *
 * @example
 * ```tsx
 * <DropdownInputInput
 *   defaultValue="Option1"
 *   onChange={handleSelectChange}
 *   options={[
 *     {label: 'Option1', value: '1'},
 *     {label: 'Option2', value: '2'}
 *   ]}
 *   popOverPosition="left"
 * />
 * ```
 */
const DropdownInput = ({
    defaultValue,
    hints = [],
    infoText,
    inputLabel,
    isDisabled = false,
    isKeepOnUnmount = false,
    name,
    onChange = () => null,
    options,
    placeholder,
    popOverPosition = 'right',
    testId = 'DropdownInputInput',
    validation = () => null
}: ComponentProps) => (
    <BaseDropdownInput
        defaultValue={defaultValue}
        hasSearch={false}
        hints={hints}
        infoText={infoText}
        inputLabel={inputLabel}
        isDisabled={isDisabled}
        isKeepOnUnmount={isKeepOnUnmount}
        name={name}
        options={options}
        placeholder={placeholder}
        popOverPosition={popOverPosition}
        testId={testId}
        validation={validation}
        onChange={onChange}
    />
);

DropdownInput.displayName = 'DropdownInput';

export {DropdownInput};